import { ToolType, type Tool } from "@repo/app-types/entities/tool";
import type { ToolProposalData } from "@repo/app-types/entities/toolProposal";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { linkStyle } from "@repo/ui/Link/index";
import { IconSparkles } from "@tabler/icons-react";
import isEqual from "lodash-es/isEqual.js";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { AttachmentDiffView } from "../agents/components/attachmentDiffView.js";
import { listToolProposalFields } from "./toolProposalCardContent.js";

interface ToolProposalBannerProps {
  data: ToolProposalData;
  /** The tool as saved now. */
  tool: Tool;
}

/** A field's value before and after the proposal. */
interface FieldChange {
  baseline: string;
  proposed: string;
}

function buildFieldChange(
  baseline: string | undefined,
  proposed: string | undefined,
): FieldChange | undefined {
  return baseline !== undefined && proposed !== undefined
    ? { baseline, proposed }
    : undefined;
}

/** Whether a field the proposal changes was saved as something else since. */
function checkBaselineStale(data: ToolProposalData, tool: Tool): boolean {
  const { baseline } = data;
  if (!baseline) {
    return false;
  }
  const savedSettings: Readonly<Record<string, unknown>> = tool.settings;
  const baselineSettings: Readonly<Record<string, unknown>> =
    baseline.settings ?? {};
  return (
    (baseline.name !== undefined && baseline.name !== tool.name) ||
    (baseline.description !== undefined &&
      baseline.description !== tool.description) ||
    Object.entries(baselineSettings).some(
      ([key, value]) => !isEqual(value, savedSettings[key]),
    )
  );
}

/**
 * The Ask Alpharun proposal in the tool's unsaved changes: what it changes,
 * the diff on request, and what the user must do before it works. Save and
 * discard are the form's own.
 */
export const ToolProposalBanner: FC<ToolProposalBannerProps> = (props) => {
  const { data, tool } = props;
  const [isShowingDiff, setIsShowingDiff] = useState(false);

  const fields = listToolProposalFields(data);
  const notes = data.notes ?? [];
  const nameChange = buildFieldChange(data.baseline?.name, data.name);
  const descriptionChange = buildFieldChange(
    data.baseline?.description,
    data.description,
  );
  const codeChange =
    data.tool_type === ToolType.CODE
      ? buildFieldChange(data.baseline?.settings?.code, data.settings?.code)
      : undefined;
  const urlChange =
    data.tool_type === ToolType.CUSTOM
      ? buildFieldChange(data.baseline?.settings?.url, data.settings?.url)
      : undefined;
  const hasDiff = Boolean(
    nameChange ?? descriptionChange ?? codeChange ?? urlChange,
  );
  const isStale = checkBaselineStale(data, tool);

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
      <div className="flex items-start gap-2">
        <IconSparkles
          aria-hidden
          className="mt-0.5 size-4 shrink-0 text-indigo-500"
        />
        <div className="min-w-0 flex-1">
          <p>{data.summary}</p>
          {fields.length > 0 ? (
            <p className="mt-0.5 text-xs text-indigo-700">
              Suggested by Ask Alpharun: {fields.join(", ")}. Save to apply
              them.
            </p>
          ) : null}
        </div>
        {hasDiff ? (
          <Button
            variant={ButtonVariant.Tertiary}
            size={Size.SMALL}
            className={twMerge("shrink-0 cursor-pointer", linkStyle())}
            onClick={() => setIsShowingDiff((current) => !current)}
          >
            {isShowingDiff ? "Hide changes" : "Show changes"}
          </Button>
        ) : null}
      </div>

      {isStale ? (
        <p className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-amber-800">
          The saved tool has changed since this suggestion was made. Saving
          replaces those changes with the suggestion.
        </p>
      ) : null}

      {notes.length > 0 ? (
        <ul className="list-disc rounded-md border border-amber-400 bg-amber-50 py-2 pr-3 pl-7 text-amber-800">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}

      {isShowingDiff && nameChange ? (
        <p>
          <span className="font-medium">Name:</span>{" "}
          <span className="line-through">{nameChange.baseline}</span> →{" "}
          {nameChange.proposed}
        </p>
      ) : null}
      {isShowingDiff && urlChange ? (
        <p className="break-all">
          <span className="font-medium">API endpoint:</span>{" "}
          <span className="line-through">{urlChange.baseline}</span> →{" "}
          {urlChange.proposed}
        </p>
      ) : null}
      {isShowingDiff && descriptionChange ? (
        <FieldDiff label="Description" change={descriptionChange} />
      ) : null}
      {isShowingDiff && codeChange ? (
        <FieldDiff label="Code" change={codeChange} />
      ) : null}
    </div>
  );
};

interface FieldDiffProps {
  label: string;
  change: FieldChange;
}

const FieldDiff: FC<FieldDiffProps> = (props) => {
  const { label, change } = props;
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium">{label}</span>
      <div className="flex max-h-96 flex-col overflow-hidden rounded-md border border-border-light bg-white text-foreground">
        <AttachmentDiffView
          savedBody={change.baseline}
          editedBody={change.proposed}
        />
      </div>
    </div>
  );
};
